"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { io } from "socket.io-client";
import WebcastingContext from './webcasting/webcastingContext';

const WebcastSocketContext = createContext();

export const useWebcastSocket = () => {


  const context = useContext(WebcastSocketContext);
  if (!context) {
    throw new Error("useWebcastSocket must be used within a WebcastSocketProvider");
  }
  return context;
};

const SOCKET_URL_ROOM = process.env.REACT_APP_SOCKET_IO_URL_ROOM

export const WebcastSocketProvider = ({ children }) => {

    const webcastingContext = useContext(WebcastingContext);
    const {
        event,
      getCurrentThumb,
      setLiveStatus,
      setLiveLayout,
      setCurrentPresenter,
      getMessageToAll,
      setQnaStatus,
      refetchEvent,
      authorization,
    } = webcastingContext;
    console.log('authorizationFromWebcastSocket', authorization)

  const authorizationRef = useRef(authorization);
  authorizationRef.current = authorization;

  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  const [currentRoom, setCurrentRoom] = useState(null);
  const currentRoomRef = useRef(null);
  const [connectionError, setConnectionError] = useState(null);
  const [isDisconnected, setIsDisconnected] = useState(false);

  // State for events received from server (webcast-room → webcast-user)
  const [presenterId, setPresenterId] = useState(null);
  const [slideId, setSlideId] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [offset, setOffset] = useState(null);
  const [lastEventMessage, setLastEventMessage] = useState(null);
  const [lastPollTriggered, setLastPollTriggered] = useState(null);
  const [lastSurveyTriggered, setLastSurveyTriggered] = useState(null);
  const eventRef = useRef(event);

  useEffect(() => {
    eventRef.current = event;
    console.log('[WebcastSocket] event updated', event);
  }, [event]);



  useEffect(() => {
    const socketInstance = io(`${SOCKET_URL_ROOM}/webcast-user`, {
      // No auth required for webcast-user namespace (viewer side)
    });

    socketInstance.on("connect", () => {
      console.log("[WebcastSocket] Connected");
      setIsDisconnected(false);
      setConnectionError(null);
    });

    socketInstance.on("connect_error", (error) => {
      console.error("[WebcastSocket] Connection error:", error);
      setConnectionError(error.message || "Failed to connect to server");
    });

    socketInstance.on("disconnect", (reason) => {
      console.log("[WebcastSocket] Disconnected:", reason);
      setIsDisconnected(true);
      setCurrentRoom(null);
      if (reason === "io server disconnect") {
        socketInstance.connect();
      }
    });

    socketInstance.io.on("reconnect_failed", () => {
      setConnectionError("Connection lost. All reconnection attempts failed.");
    });

    // --- Events emitted from webcast-room to webcast-user ---
    socketInstance.on("presenterChanged", ({ presenter }) => {
      const currentEvent = eventRef.current;
      if (currentEvent && presenter) {
        console.log('[presenterChanged] event:', currentEvent._id, presenter);
        setCurrentPresenter(presenter);
        setPresenterId(presenter._id || presenter.id);
      }
    });

    socketInstance.on("slideChanged", ({ slideId: id }) => {
      const currentEvent = eventRef.current;
      if (currentEvent && id) {
        console.log('[slideChanged] event:', currentEvent._id, id);
        setLiveStatus('slide');
        setSlideId(id);
        getCurrentThumb(id);
      }
    });

    socketInstance.on("fullScreenEnabled", () => {
      const currentEvent = eventRef.current;
      if (currentEvent) {
        setQnaStatus(true);
        setIsFullScreen(true);
      }
    });

    socketInstance.on("fullScreenDisabled", () => {
      const currentEvent = eventRef.current;
      if (currentEvent) {
        setQnaStatus(false);
        setIsFullScreen(false);
      }
    });

    socketInstance.on("offsetChanged", ({ eventId: _eventId, offset: o }) => {
      const currentEvent = eventRef.current;
      if (currentEvent && o != null) {
        setOffset(o);
      }
    });

    socketInstance.on("forceRefresh", ({ eventId }) => {
      const currentEvent = eventRef.current;
      if (currentEvent && eventId) {
        console.log('[forceRefresh]', eventId);
        refetchEvent(eventId);
      }
    });

    socketInstance.on("userKicked", ({ email, eventId }) => {
      const auth = authorizationRef.current;
      console.log('[userKicked]', email, eventId, auth);
      let fields = null;
      try {
        const fieldsRaw = auth?.fields ?? auth?.registrants?.[0]?.fields;
        if (typeof fieldsRaw === 'string') {
          fields = JSON.parse(fieldsRaw);
        } else if (fieldsRaw && typeof fieldsRaw === 'object') {
          fields = fieldsRaw;
        }
      } catch (_) {}
      const localEmail = String(fields?.email || '').toLowerCase().trim();
      const kickedEmail = String(email || '').toLowerCase().trim();
      if (localEmail && kickedEmail && localEmail === kickedEmail) {
        // Delay hard redirect so other listeners (e.g. leaveEventByEmail) can flush first
        localStorage.removeItem('localData');
        setTimeout(() => {
          window.location.href = `/broadcast/${eventId}`;
        }, 150);
      }
    });

    socketInstance.on("eventMessage", ({ eventId, message }) => {
      const currentEvent = eventRef.current;
      if (currentEvent && message) {
        console.log('[eventMessage]', eventId, message);
        getMessageToAll(message);
        setLiveStatus('messageArrived');
        setLastEventMessage({ eventId, message, at: Date.now() });
      }
    });

    //clearAllBroadcastMessages
    socketInstance.on("clearAllBroadcastMessages", () => {
      const currentEvent = eventRef.current;
      if (currentEvent) {
        getMessageToAll([]);
      }
    });

    socketInstance.on("pollTriggered", ({ eventId }) => {
      const currentEvent = eventRef.current;
      if (currentEvent && eventId) {
        setLastPollTriggered({ eventId, at: Date.now() });
      }
    });

    socketInstance.on("surveyTriggered", ({ eventId }) => {
      const currentEvent = eventRef.current;
      if (currentEvent && eventId) {
        setLastSurveyTriggered({ eventId, at: Date.now() });
      }
    });

    socketInstance.on("eventStarted", () => {
      console.log('[eventStartedwowoow]');
      const currentEvent = eventRef.current;
      if (eventRef.current) {
        setLiveStatus('start');
        setLiveLayout('start');
        refetchEvent(currentEvent?._id);
      }
    });

    socketInstance.on("eventStopped", () => {
      if (eventRef.current) {
        setLiveStatus('stop');
        setLiveLayout('stop');
      }
    });

    socketInstance.on("eventPaused", () => {
      if (eventRef.current) {
        setLiveStatus('stop');
        setLiveLayout('stop');
      }
    });

    socketInstance.on("eventResumed", () => {
      if (eventRef.current) {
        setLiveStatus('start');
        setLiveLayout('start');
      }
    });

    socketInstance.on("eventRestarted", () => {
      if (eventRef.current) {
        setLiveStatus('reset');
        setLiveLayout('reset');
        refetchEvent(eventRef.current?._id);
      }
    });

    setSocket(socketInstance);
    socketRef.current = socketInstance;

    return () => {
      if (socketInstance) {
        socketInstance.disconnect();
      }
    };
  }, []);

  // ── Page Visibility / App-switch recovery ───────────────────────────────
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const s = socketRef.current;
      if (!s) return;

      if (!s.connected) {
        // Socket dropped while the tab was hidden — reconnect
        console.log('[WebcastSocket] Page visible, socket disconnected — reconnecting');
        setIsDisconnected(true);
        s.connect();
      } else if (currentRoomRef.current) {
        // Socket is alive but the server may have evicted us from the room
        // (e.g. server restart while tab was hidden) — silently re-join
        console.log('[WebcastSocket] Page visible, re-joining room', currentRoomRef.current);
        s.emit('joinRoom', { eventId: currentRoomRef.current });
      }
    };

    // visibilitychange covers screen-off and app-switch on every modern mobile browser
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // focus fires on desktop and on some browsers when the user returns to the tab
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, []);

  const joinRoom = useCallback(
    (eventId) => {
      if (!socket || !eventId) return;
      if (currentRoom === eventId) return;
      socket.emit("joinRoom", { eventId });
      setCurrentRoom(eventId);
      currentRoomRef.current = eventId;
    },
    [socket, currentRoom]
  );

  const leaveRoom = useCallback(
    (eventId) => {
      if (!socket || !eventId) return;
      if (currentRoom !== eventId) return;
      socket.emit("leaveRoom", { eventId });
      setCurrentRoom(null);
      currentRoomRef.current = null;
    },
    [socket, currentRoom]
  );


    const signalNewQuestion = useCallback(
      (eventId, question) => {

        console.log("Signal new question:", eventId, question);
        if (!socket) return;
        socket.emit("newQuestion", { eventId, question });
      },
      [socket],
    );

  const reconnectSocket = useCallback(() => {
    if (socket) {
      socket.connect();
    }
  }, [socket]);

  const value = {
    socket,
    currentRoom,
    connectionError,
    isDisconnected,
    joinRoom,
    leaveRoom,
    reconnectSocket,
    // Synced state from server
    presenterId,
    slideId,
    isFullScreen,
    offset,
    lastEventMessage,
    lastPollTriggered,
    lastSurveyTriggered,
    signalNewQuestion,
  };

  return (
    <WebcastSocketContext.Provider value={value}>
      {children}
    </WebcastSocketContext.Provider>
  );
};
